import { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Close, SaveOutlined, PersonAdd, Edit as EditIcon } from '@mui/icons-material';
import * as yup from 'yup';
import api from '../../../Services/api';
import { useToast } from '../../../Hooks/toast';
import { PermissionConfiguration } from './OperatorForm/components/PermissionConfiguration';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void; // backwards compatibility
  onSaved?: () => void;   // preferred callback
  operatorId?: string;    // when provided, modal works in edit mode
  defaultProfile?: 'admin' | 'user' | 'subAccount';
  defaultSubAccountId?: string;
};

const validationSchema = yup.object().shape({
  name: yup.string().required('O campo Nome é obrigatório.'),
  email: yup
    .string()
    .email('Insira um e-mail valido.')
    .required('O campo E-mail é obrigatório.'),
  phone: yup
    .string()
    .min(11, 'Telefone deve contar 11 dígitos')
    .required('O campo Telefone é obrigatório.'),
  profile: yup.string().required('O campo Tipo de usuário é obrigatório.'),
});

const initialOperator = {
  name: '',
  email: '',
  phone: '',
  profile: 'admin',
  subAccountId: '',
  isActive: true,
  permissions: [] as string[],
  ranges: {} as any,
};

const CreateOperatorModal = ({ open, onClose, onCreated, onSaved, operatorId, defaultProfile, defaultSubAccountId }: Props) => {
  const { addToast } = useToast();

  const [operator, setOperator] = useState({ ...initialOperator });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [subAccounts, setSubAccounts] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<PermissionGroup[]>([]);
  const [saving, setSaving] = useState(false);

  const handleChange = (e: ChangeEvent<{ value: string; name: string }>) => {
    const { name, value } = e.target as any;
    setOperator((prev) => ({ ...prev, [name]: value }));
  };

  const resetState = () => {
    setOperator({ ...initialOperator });
    setErrors({});
  };

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        const [permissionsRes, subaccountsRes] = await Promise.all([
          api.get('operators/permissions/Groupped'),
          api.get('subaccount'),
        ]);
        setPermissions(permissionsRes.data || []);
        setSubAccounts(subaccountsRes.data || []);

        if (operatorId) {
          const { data } = await api.get(`operators/${operatorId}`);
          setOperator({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            profile: data.profile || 'admin',
            subAccountId: data.subAccountId || '',
            isActive: !!data.isActive,
            permissions: data.permissions || [],
            ranges: data.ranges || {},
          });
        } else {
          setOperator({
            ...initialOperator,
            profile: defaultProfile || 'admin',
            subAccountId: defaultSubAccountId || '',
          });
        }
      } catch (e) {
        // ignore
      }
    };

    load();
    // Proactively close any lingering tooltips when opening this dialog
    try { window.dispatchEvent(new CustomEvent('app-tooltip-close-all')); } catch { /* ignore */ }
  }, [open, operatorId]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await validationSchema.validate(operator, { abortEarly: false });

      const body: any = { ...operator, role: operator.profile };
      if (!body.subAccountId) body.subAccountId = null;

      if (operatorId) {
        await api.put(`/operators/${operatorId}`, body);
      } else {
        await api.post(`/operators`, body);
      }

      setErrors({});
      addToast({ title: 'Sucesso', description: 'Operador salvo com sucesso', type: 'success' });

      onSaved?.();
      onCreated?.();
      resetState();
      onClose();
    } catch (err: any) {
      if (err && err.name === 'ValidationError') {
        const newErrors: Record<string, string> = {};
        err.inner?.forEach((inner: any) => {
          if (inner?.path) newErrors[inner.path] = inner.message;
        });
        setErrors(newErrors);
      } else {
        addToast({ title: 'Erro', description: 'Erro ao salvar o operador', type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetState();
        onClose();
      }}
      maxWidth={operator.profile === 'user' ? 'lg' : 'sm'}
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          transition: theme.transitions.create(['max-width', 'width'], {
            duration: 180,
            easing: theme.transitions.easing.easeOut,
          }),
        }),
      }}
      aria-labelledby="create-operator-title"
    >
      <DialogTitle
        id="create-operator-title"
        sx={{
          bgcolor: 'primary.main',
          color: 'common.white',
          textAlign: 'center',
          py: 2.5,
          fontSize: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 60,
        }}
      >
        {operatorId ? (
          <EditIcon sx={{ mr: 1 }} fontSize="medium" color="inherit" />
        ) : (
          <PersonAdd sx={{ mr: 1 }} fontSize="medium" color="inherit" />
        )}
        {operatorId ? 'Editar operador' : 'Novo operador'}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, pt: 0 }}>
        <Grid container component="form" id="frmCreateOperator" onSubmit={onSubmit} spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome"
              name="name"
              helperText={errors.name}
              error={!!errors.name}
              value={operator.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="E-mail"
              name="email"
              helperText={errors.email}
              error={!!errors.email}
              value={operator.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Telefone"
              name="phone"
              helperText={errors.phone}
              error={!!errors.phone}
              value={operator.phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tipo de usuário"
              name="profile"
              helperText={errors.profile}
              error={!!errors.profile}
              value={operator.profile}
              onChange={handleChange}
              select
            >
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="user">Usuário</MenuItem>
              <MenuItem value="subAccount">Operador</MenuItem>
            </TextField>
          </Grid>
          {operatorId && (
            <Grid item xs={12}>
              <FormControlLabel
                checked={operator.isActive}
                onChange={() => setOperator((o) => ({ ...o, isActive: !o.isActive }))}
                control={<Checkbox defaultChecked />}
                label="Ativado?"
              />
            </Grid>
          )}
          {(operator.profile === 'subAccount' || (!!operator.subAccountId && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/i.test(operator.subAccountId))) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ponto de venda"
                name="subAccountId"
                helperText={errors.subAccountId}
                error={!!errors.subAccountId}
                value={operator.subAccountId}
                onChange={handleChange}
                select
              >
                {subAccounts.map((subAccount: any, i: number) => (
                  <MenuItem value={subAccount.subAccountId} key={i}>
                    {subAccount.name} - {subAccount.subAccountId}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {operator.profile === 'user' && (
            <Grid item xs={12}>
              <PermissionConfiguration
                permissionGroups={permissions}
                onChange={(perms) => setOperator((o) => ({ ...o, permissions: perms }))}
                userPermissions={operator.permissions}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { resetState(); onClose(); }} color="error" variant="text" startIcon={<Close />}>
          Cancelar
        </Button>
        <Button
          form="frmCreateOperator"
          variant="contained"
          color="primary"
          type="submit"
          disabled={saving}
          startIcon={<SaveOutlined />}
          sx={{ color: 'white' }}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOperatorModal;
